import Papa from 'papaparse'

export interface FeriasCargaRow {
  compradorPrincipalEmail: string
  compradorFeriasEmail: string
  fornecedorId: string
  dataInicio: string // yyyy-MM-dd (input date format)
  dataFim: string // yyyy-MM-dd
  // Linha real no arquivo/Excel (cabeçalho = linha 1; linhas vazias contam)
  _lineNumber: number
  errors: string[]
}

type FeriasCargaField = keyof Omit<FeriasCargaRow, '_lineNumber' | 'errors'>

// Limites da carga (evita travar o navegador no preview)
export const FERIAS_CARGA_MAX_FILE_BYTES = 5 * 1024 * 1024
export const FERIAS_CARGA_MAX_ROWS = 5000

const pad2 = (n: string) => n.padStart(2, '0')

// Data válida de verdade (31/02 não existe): confere o round-trip dia/mês/ano
function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  const d = new Date(year, month - 1, day)
  return (
    d.getFullYear() === year &&
    d.getMonth() === month - 1 &&
    d.getDate() === day
  )
}

// d/M/yyyy, dd/MM/yyyy (com hora opcional) ou yyyy-MM-dd → yyyy-MM-dd
// Se não reconhecer, devolve o texto original (a validação acusa o erro)
function toInputDate(raw: string): string {
  const trimmed = raw.trim()
  const br = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+.*)?$/)
  if (br) return `${br[3]}-${pad2(br[2])}-${pad2(br[1])}`
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/)
  if (iso) return `${iso[1]}-${pad2(iso[2])}-${pad2(iso[3])}`
  return trimmed
}

function isValidInputDate(value: string): boolean {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return false
  return isRealDate(Number(m[1]), Number(m[2]), Number(m[3]))
}

function normalizeHeader(h: string): string {
  return h
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '')
}

// Remove o apóstrofo que o export acrescenta para bloquear fórmulas no Excel
const cleanCell = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/^'(?=[=+\-@])/, '')

const HEADER_MAP: Record<string, FeriasCargaField> = {
  // e-mail do comprador principal/titular
  compradorprincipalemail: 'compradorPrincipalEmail',
  compradortitularemail: 'compradorPrincipalEmail',
  titularemail: 'compradorPrincipalEmail',
  compradorbackup: 'compradorPrincipalEmail',
  compradorbackupemail: 'compradorPrincipalEmail',
  emailprincipal: 'compradorPrincipalEmail',
  // e-mail do comprador de férias/substituto
  compradorferiasemail: 'compradorFeriasEmail',
  compradorsubstitutoemail: 'compradorFeriasEmail',
  substitutoemail: 'compradorFeriasEmail',
  compradorferias: 'compradorFeriasEmail',
  emailferias: 'compradorFeriasEmail',
  // canonical / "Fornecedor" (export header, normalizes to fornecedor)
  fornecedorid: 'fornecedorId',
  fornecedor: 'fornecedorId',
  // dates — "Data Inicio" and "Data Fim" normalize to datainicio / datafim
  datainicio: 'dataInicio',
  datafim: 'dataFim'
}

export function parseFeriasCargaCSV(text: string): {
  rows: FeriasCargaRow[]
  parseError: string | null
} {
  const clean = text.replace(/^\uFEFF/, '')
  const firstLine = clean.split(/\r?\n/, 1)[0] ?? ''
  const sep = firstLine.includes(';') ? ';' : ','

  // Sem skipEmptyLines: o índice de cada linha precisa bater com a linha do Excel
  const parsed = Papa.parse(clean, {
    delimiter: sep,
    skipEmptyLines: false
  })
  const lines = parsed.data as string[][]
  const isEmptyLine = (cols: string[]) =>
    !cols || cols.every(c => cleanCell(c) === '')

  if (lines.length === 0 || isEmptyLine(lines[0])) {
    return { rows: [], parseError: 'Arquivo vazio ou sem cabeçalho.' }
  }

  const headers = lines[0].map(h => normalizeHeader(cleanCell(h)))

  const colIdx = (key: FeriasCargaField): number => {
    for (const [alias, field] of Object.entries(HEADER_MAP)) {
      if (field === key && headers.includes(alias)) {
        return headers.indexOf(alias)
      }
    }
    return -1
  }

  const idxPrincipal = colIdx('compradorPrincipalEmail')
  const idxFerias = colIdx('compradorFeriasEmail')
  const idxFornecedor = colIdx('fornecedorId')
  const idxInicio = colIdx('dataInicio')
  const idxFim = colIdx('dataFim')

  if (
    [idxPrincipal, idxFerias, idxFornecedor, idxInicio, idxFim].includes(-1)
  ) {
    return {
      rows: [],
      parseError:
        'Colunas obrigatórias não encontradas. Esperado: CompradorPrincipalEmail (ou Comprador Backup), CompradorFeriasEmail (ou Comprador Ferias), Fornecedor (ou FornecedorId), Data Inicio, Data Fim.'
    }
  }

  const rows: FeriasCargaRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i]
    if (isEmptyLine(cols)) continue
    rows.push({
      compradorPrincipalEmail: cleanCell(cols[idxPrincipal]),
      compradorFeriasEmail: cleanCell(cols[idxFerias]),
      fornecedorId: cleanCell(cols[idxFornecedor]),
      dataInicio: toInputDate(cleanCell(cols[idxInicio])),
      dataFim: toInputDate(cleanCell(cols[idxFim])),
      // índice 0 = cabeçalho = linha 1 do Excel
      _lineNumber: i + 1,
      errors: []
    })
  }

  if (rows.length === 0) {
    return { rows: [], parseError: 'Arquivo vazio ou sem dados.' }
  }

  return { rows: validateFeriasRows(rows), parseError: null }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const normEmail = (email: string) => (email ?? '').trim().toLowerCase()

// Validações que dependem só da própria linha
export function validateFeriasRowFields(r: FeriasCargaRow): string[] {
  const errors: string[] = []
  const principal = normEmail(r.compradorPrincipalEmail)
  const ferias = normEmail(r.compradorFeriasEmail)

  if (!principal || !EMAIL_RE.test(principal)) {
    errors.push('CompradorPrincipalEmail inválido')
  }

  if (!ferias || !EMAIL_RE.test(ferias)) {
    errors.push('CompradorFeriasEmail inválido')
  }

  if (principal && ferias && principal === ferias) {
    errors.push(
      'CompradorFeriasEmail não pode ser igual ao CompradorPrincipalEmail'
    )
  }

  const fornecedorTxt = (r.fornecedorId ?? '').trim()
  const fornecedor = Number(fornecedorTxt)
  if (!fornecedorTxt || !Number.isInteger(fornecedor) || fornecedor <= 0) {
    errors.push('FornecedorId inválido')
  }

  const inicioOk = !!r.dataInicio && isValidInputDate(r.dataInicio)
  const fimOk = !!r.dataFim && isValidInputDate(r.dataFim)
  if (!inicioOk) errors.push('Data Início inválida')
  if (!fimOk) errors.push('Data Fim inválida')

  // yyyy-MM-dd compara corretamente como texto
  if (inicioOk && fimOk && r.dataFim < r.dataInicio) {
    errors.push('Data Fim deve ser posterior à Data Início')
  }

  return errors
}

const duplicateKey = (r: FeriasCargaRow) =>
  [
    normEmail(r.compradorPrincipalEmail),
    normEmail(r.compradorFeriasEmail),
    (r.fornecedorId ?? '').trim(),
    r.dataInicio,
    r.dataFim
  ].join('|')

// Mesmo titular + fornecedor: dois períodos não podem se sobrepor
const overlapKey = (r: FeriasCargaRow) =>
  `${normEmail(r.compradorPrincipalEmail)}|${(r.fornecedorId ?? '').trim()}`

const hasValidPeriod = (r: FeriasCargaRow) =>
  isValidInputDate(r.dataInicio) &&
  isValidInputDate(r.dataFim) &&
  r.dataInicio <= r.dataFim

const overlaps = (a: FeriasCargaRow, b: FeriasCargaRow) =>
  a.dataInicio <= b.dataFim && b.dataInicio <= a.dataFim

// Validações entre linhas (duplicidade e sobreposição) para UMA linha
export function crossRowErrors(
  r: FeriasCargaRow,
  rows: FeriasCargaRow[]
): string[] {
  const errors: string[] = []
  const dupKey = duplicateKey(r)
  const ovKey = overlapKey(r)
  const periodOk = hasValidPeriod(r)

  let duplicateOf: number | undefined
  let overlapWith: number | undefined
  for (const other of rows) {
    if (other._lineNumber === r._lineNumber) continue
    if (duplicateKey(other) === dupKey) {
      // Aponta só a partir da segunda ocorrência (a primeira é a original)
      if (other._lineNumber < r._lineNumber && duplicateOf === undefined) {
        duplicateOf = other._lineNumber
      }
      continue
    }
    if (
      periodOk &&
      overlapWith === undefined &&
      overlapKey(other) === ovKey &&
      hasValidPeriod(other) &&
      overlaps(r, other)
    ) {
      overlapWith = other._lineNumber
    }
  }

  if (duplicateOf !== undefined) {
    errors.push(`Duplicado: mesmo registro na linha ${duplicateOf}`)
  }
  if (overlapWith !== undefined) {
    errors.push(
      `Período (Data Início/Data Fim) sobreposto ao da linha ${overlapWith} para o mesmo comprador e fornecedor`
    )
  }
  return errors
}

export function validateFeriasRow(
  r: FeriasCargaRow,
  rows: FeriasCargaRow[]
): FeriasCargaRow {
  return {
    ...r,
    errors: [...validateFeriasRowFields(r), ...crossRowErrors(r, rows)]
  }
}

export function validateFeriasRows(rows: FeriasCargaRow[]): FeriasCargaRow[] {
  // Agrupa por titular+fornecedor para comparar só linhas que podem conflitar
  const groups = new Map<string, FeriasCargaRow[]>()
  for (const r of rows) {
    const key = overlapKey(r)
    const group = groups.get(key)
    if (group) group.push(r)
    else groups.set(key, [r])
  }

  return rows.map(r => validateFeriasRow(r, groups.get(overlapKey(r)) ?? []))
}
